import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { getApiBaseUrl } from "../utils/contactApi";

const AI_HUB_PATH = "hubs/aichat";
const RECONNECT_DELAYS = [0, 2000, 5000, 10000];

const getAiHubUrl = () => `${getApiBaseUrl()}/${AI_HUB_PATH}`;

const createMessageId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const useSignalrAiChat = () => {
  const [connectionStatus, setConnectionStatus] = useState("idle");
  const [messages, setMessages] = useState([
    {
      id: createMessageId(),
      role: "assistant",
      content: "שלום, אני CodeIL Architect. תארו את הרעיון שלכם ונתחיל לבנות לכם כיוון טכנולוגי.",
    },
  ]);
  const [streamingChunk, setStreamingChunk] = useState("");
  const [isReceiving, setIsReceiving] = useState(false);

  const connectionRef = useRef(null);
  const chunkRef = useRef("");
  const messagesRef = useRef(messages);
  const isIntentionalStopRef = useRef(false);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const flushStreamToMessage = useCallback((fallbackText = "") => {
    const finalContent = `${chunkRef.current}${fallbackText}`.trim();
    if (!finalContent) {
      setIsReceiving(false);
      return;
    }

    setMessages((previousMessages) => [
      ...previousMessages,
      {
        id: createMessageId(),
        role: "assistant",
        content: finalContent,
      },
    ]);

    chunkRef.current = "";
    setStreamingChunk("");
    setIsReceiving(false);
  }, []);

  const startConnection = useCallback(async () => {
    if (connectionRef.current?.state === HubConnectionState.Connected) {
      return true;
    }

    if (connectionRef.current?.state === HubConnectionState.Connecting) {
      return false;
    }

    isIntentionalStopRef.current = false;
    setConnectionStatus("connecting");

    try {
      // Always build a fresh connection — SignalR instances cannot be restarted
      // after stop() is called (e.g. React 18 StrictMode cleanup cycle).
      const connection = new HubConnectionBuilder()
        .withUrl(getAiHubUrl())
        .withAutomaticReconnect(RECONNECT_DELAYS)
        .configureLogging(LogLevel.Warning)
        .build();

      connectionRef.current = connection;

      connection.off("ReceiveMessageChunk");
      connection.off("ReceiveMessageCompleted");

      connection.on("ReceiveMessageChunk", (chunk) => {
        const safeChunk = typeof chunk === "string" ? chunk : "";
        setIsReceiving(true);
        chunkRef.current = `${chunkRef.current}${safeChunk}`;
        setStreamingChunk(chunkRef.current);
      });

      connection.on("ReceiveMessageCompleted", (finalText) => {
        const safeFinalText = typeof finalText === "string" ? finalText : "";
        flushStreamToMessage(safeFinalText);
      });

      connection.onreconnecting(() => {
        setConnectionStatus("connecting");
      });

      connection.onreconnected(() => {
        setConnectionStatus("connected");
      });

      connection.onclose(() => {
        if (isIntentionalStopRef.current) {
          setConnectionStatus("idle");
          return;
        }

        setConnectionStatus("error");

        if (chunkRef.current) {
          flushStreamToMessage();
        }
      });

      await connection.start();
      setConnectionStatus("connected");
      return true;
    } catch (error) {
      setConnectionStatus("error");
      return false;
    }
  }, [flushStreamToMessage]);

  const stopConnection = useCallback(async () => {
    const connection = connectionRef.current;
    if (!connection) {
      setConnectionStatus("idle");
      return;
    }

    isIntentionalStopRef.current = true;

    try {
      if (connection.state !== HubConnectionState.Disconnected) {
        await connection.stop();
      }
      setConnectionStatus("idle");
    } catch (error) {
      setConnectionStatus("error");
    }
  }, []);

  useEffect(() => {
    startConnection();

    return () => {
      stopConnection();
    };
  }, [startConnection, stopConnection]);

  const appendUserMessage = useCallback((content) => {
    const nextHistory = [
      ...messagesRef.current,
      {
        id: createMessageId(),
        role: "user",
        content,
      },
    ];

    messagesRef.current = nextHistory;
    setMessages(nextHistory);
    return nextHistory;
  }, []);

  const sendMessage = useCallback(
    async (history) => {
      if (!Array.isArray(history) || history.length === 0) {
        return false;
      }

      const ready =
        connectionRef.current?.state === HubConnectionState.Connected ||
        (await startConnection());

      if (!ready || !connectionRef.current) {
        setConnectionStatus("error");
        return false;
      }

      try {
        setIsReceiving(true);
        setStreamingChunk("");
        chunkRef.current = "";

        const normalizedHistory = history.map((item) => ({
          role: item.role,
          content: item.content,
        }));

        await connectionRef.current.invoke("sendMessage", normalizedHistory);
        return true;
      } catch (error) {
        setConnectionStatus("error");
        setIsReceiving(false);

        if (chunkRef.current) {
          flushStreamToMessage();
        }

        return false;
      }
    },
    [flushStreamToMessage, startConnection]
  );

  return useMemo(
    () => ({
      connectionStatus,
      messages,
      streamingChunk,
      isReceiving,
      appendUserMessage,
      sendMessage,
      startConnection,
      stopConnection,
    }),
    [
      appendUserMessage,
      connectionStatus,
      isReceiving,
      messages,
      sendMessage,
      startConnection,
      stopConnection,
      streamingChunk,
    ]
  );
};

export default useSignalrAiChat;
